import React, { useState } from "react";
import PropTypes from "prop-types";
import { EyeOutlined, EyeInvisibleOutlined } from "@/config/antd";
import { useTheme } from "@/hooks/ThemeContext";
import BaseInput from "@/components/common/inputs/Base_Input";
import {
  getInputBorderClass,
  getInputBackgroundClass,
  getIconColorClass,
  getFocusClasses,
  getTransitionClasses,
  getBasePaddingClasses,
  isInputActive,
} from "@/helper/input_styles";

const TextInput = ({
  label,
  placeholder,
  icon,
  type = "text",
  value = "",
  onChange,
  className = "",
  currencyFormat = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { theme } = useTheme();

  const toggleShowPassword = () => setShowPassword(!showPassword);
  const inputElementType =
    type === "password" && !showPassword ? "password" : "text";

  const isActive = isInputActive(value, isFocused);
  const iconColor = getIconColorClass(isActive);
  const passwordToggleColor =
    theme === "dark" && isActive ? "text-black" : iconColor;

  const formatForDisplay = (val) => {
    if (val === undefined || val === null || val === "") return "";
    const parts = val.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    parts[1] = (parts[1] || "").padEnd(2, "0");
    return `${parts[0]}.${parts[1].substring(0, 2)}`;
  };

  const handleValueChange = (e) => {
    if (type === "number") {
      let inputValue = e.target.value;

      inputValue = inputValue.replace(/[^0-9.]/g, "");

      const parts = inputValue.split(".");
      if (parts.length > 2) {
        inputValue = parts[0] + "." + parts.slice(1).join("");
      }

      if (parts[1] && parts[1].length > 2) {
        inputValue = parts[0] + "." + parts[1].substring(0, 2);
      }

      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: inputValue },
      };
      onChange(syntheticEvent);
    } else {
      onChange(e);
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    if (currencyFormat) {
      e.target.select();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const displayValue =
    type === "number" && currencyFormat && !isFocused
      ? formatForDisplay(value)
      : value || "";

  return (
    <BaseInput label={label} isActive={isActive}>
      <div className="absolute inset-y-0 left-3 flex items-center">
        {icon && (
          <span
            className={`pr-3 border-r border-gray-300`}
            data-testid="input-icon">
            {React.isValidElement(icon)
              ? React.cloneElement(icon, { className: iconColor })
              : icon}
          </span>
        )}
      </div>
      <input
        type={inputElementType}
        className={`w-full ${getBasePaddingClasses(!!icon)} 
          ${getInputBackgroundClass(isActive)}
          rounded-md leading-5 text-gray-900 preserve-text-primary placeholder-gray-400 
          ${getInputBorderClass(isActive)}
          ${getFocusClasses()}
          ${getTransitionClasses()} ${className} 
          ${
            type === "password"
              ? `password-input ${showPassword ? "show-password" : ""}`
              : ""
          }`}
        placeholder={placeholder}
        value={displayValue}
        onChange={handleValueChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid="input-field"
        {...props}
      />
      {type === "password" && (
        <button
          type="button"
          onClick={toggleShowPassword}
          className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${passwordToggleColor}`}
          data-testid="password-toggle">
          {showPassword ? (
            <EyeInvisibleOutlined data-testid="eye-invisible-icon" />
          ) : (
            <EyeOutlined data-testid="eye-icon" />
          )}
        </button>
      )}
    </BaseInput>
  );
};

TextInput.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  icon: PropTypes.element,
  type: PropTypes.oneOf(["text", "password", "tel", "email", "number"]),
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  currencyFormat: PropTypes.bool,
};

export default TextInput;
